"use client";

import Message from "@/components/editor/ai/message";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ChatMessage } from "@/types/ai";
import { ThemeStyles } from "@/types/theme";
import { defaultPresets } from "@/utils/theme-presets";
import { useEffect, useRef } from "react";

export function AIChatDemo({
  disabled = true,
  className,
}: {
  disabled?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    // Always block tabbing: set tabIndex="-1" on all focusable children
    const focusables = ref.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusables.forEach((el) => {
      (el as HTMLElement).setAttribute("tabindex", "-1");
      // Only set disabled if supported and disabled is true
      if (disabled && "disabled" in el) (el as HTMLButtonElement).disabled = true;
      if (!disabled && "disabled" in el) (el as HTMLButtonElement).disabled = false;
    });
  }, [disabled]);

  return (
    <div
      className={cn(
        "bg-background/50 flex h-full w-full min-w-[350px] origin-top-left flex-col overflow-hidden backdrop-blur-lg"
      )}
      aria-hidden="true"
      tabIndex={-1}
    >
      {/* Scrollable parent */}
      <ScrollArea className="flex-1">
        {/* Non-interactive chat content */}
        <div
          ref={ref}
          className={cn(
            "flex flex-col gap-4 p-4 select-none",
            disabled ? "pointer-events-none" : "",
            className
          )}
          aria-hidden="true"
          tabIndex={-1}
        >
          {CHAT_PLACEHOLDER_MESSAGES.map((msg) => (
            <Message
              key={msg.id}
              isGeneratingTheme={false}
              isLastMessageStreaming={false}
              message={msg}
              onRetry={() => {}}
              isEditing={false}
              onEdit={() => {}}
              onEditSubmit={() => {}}
              onEditCancel={() => {}}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

const CHAT_PLACEHOLDER_MESSAGES: ChatMessage[] = [
  {
    id: "1",
    role: "user",
    parts: [
      {
        type: "text",
        text: "Você pode gerar um tema a partir desta imagem?",
      },
    ],
    metadata: {
      promptData: {
        content: "Gerar um tema a partir desta imagem.",
        mentions: [],
        images: [
          {
            url: "/og-image.v050725.png",
          },
        ],
      },
    },
  },
  {
    id: "2",
    role: "assistant",
    parts: [
      {
        type: "text",
        text: "Eu gerei um tema Midnight Bloom com base na sua imagem. Ele apresenta roxos e azuis profundos para um visual calmo e moderno.",
      },
    ],
    metadata: {
      themeStyles: defaultPresets["midnight-bloom"].styles as ThemeStyles,
    },
  },
  {
    id: "3",
    role: "user",
    parts: [
      {
        type: "text",
        text: "Você pode gerar um tema inspirado no @Twitter?",
      },
    ],
  },
  {
    id: "4",
    role: "assistant",
    parts: [
      {
        type: "text",
        text: "Tudo certo, eu criei um tema inspirado no Twitter. Espere azuis brilhantes e contrastes limpos para uma vibração social e energética.",
      },
    ],
    metadata: {
      themeStyles: defaultPresets["twitter"].styles as ThemeStyles,
    },
  },
  {
    id: "5",
    role: "user",
    parts: [
      {
        type: "text",
        text: "Que tal um tema @Supabase?",
      },
    ],
  },
  {
    id: "6",
    role: "assistant",
    parts: [
      {
        type: "text",
        text: "Eu gerei um tema Supabase para você. Ele usa verdes frescos e fundos escuros para uma sensação moderna e amigável para desenvolvedores.",
      },
    ],
    metadata: {
      themeStyles: defaultPresets["supabase"].styles as ThemeStyles,
    },
  },
];
